import React from "react";
import { INSTRUCTIONS_MAX_POINTS, INSTRUCTIONS_MAX_CHARS } from "../constants";

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}
function relLuminance([r, g, b]) {
  const [rl, gl, bl] = [r, g, b].map((c) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
}
function contrastRatio(hex1, hex2) {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);
  if (!c1 || !c2) return null;
  const L1 = relLuminance(c1);
  const L2 = relLuminance(c2);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

const COLOR_FIELDS = [
  { key: "textColor", label: "Text colour" },
  { key: "headingColor", label: "Heading colour" },
  { key: "buttonBgColor", label: "Button background colour" },
  { key: "buttonTextColor", label: "Button text colour" },
];

function GameBasics({ hunt, updateHunt }) {
  const buttonContrast = contrastRatio(hunt.buttonBgColor, hunt.buttonTextColor);
  const passesButtonContrast = buttonContrast != null && buttonContrast >= 4.5;

  // A legacy hunt can still have the old fixed 6-blank-slot array — collapse that down to a
  // single empty starter row instead of always showing all 6. But once there's real content,
  // keep the array exactly as stored (including a row someone just added and hasn't typed into
  // yet) — filtering blanks out on every render would erase a freshly-added row the instant it
  // appeared, since a brand new row is blank by definition.
  const rawInstructions = (Array.isArray(hunt.instructions) ? hunt.instructions : []).map((s) => String(s ?? ""));
  const hasAnyContent = rawInstructions.some((s) => s.trim() !== "");
  const instructions = hasAnyContent ? rawInstructions : [""];

  const setInstruction = (idx, value) => {
    const next = [...instructions];
    next[idx] = value.slice(0, INSTRUCTIONS_MAX_CHARS);
    updateHunt({ instructions: next });
  };
  const addInstruction = () => {
    if (instructions.length >= INSTRUCTIONS_MAX_POINTS) return;
    updateHunt({ instructions: [...instructions, ""] });
  };
  const removeInstruction = (idx) => {
    if (instructions.length <= 1) return;
    updateHunt({ instructions: instructions.filter((_, i) => i !== idx) });
  };

  return (
    <div className="studio-step-page" data-tour="studio-step-basics">
      <h2 className="studio-h2">Game basics</h2>
      <p className="studio-sub">Name the hunt, attach the client's identity, and pick the visual theme.</p>

      <div className="studio-two-col">
        <div className="studio-col-main">
          <div className="studio-field">
            <label>Hunt name *</label>
            <input
              type="text"
              className="studio-input"
              value={hunt.huntName}
              onChange={(e) => updateHunt({ huntName: e.target.value })}
              placeholder="e.g. Sankalp Hunt 2026"
            />
            <div className="studio-hint">Participants see this on the landing screen.</div>
          </div>

          <div className="studio-field">
            <label>Client *</label>
            <input
              type="text"
              className="studio-input"
              value={hunt.client}
              onChange={(e) => updateHunt({ client: e.target.value })}
              placeholder="e.g. Client — Department"
            />
          </div>

          <div className="studio-field">
            <label>Title *</label>
            <input
              type="text"
              className="studio-input"
              value={hunt.title}
              onChange={(e) => updateHunt({ title: e.target.value })}
              placeholder="Landing screen headline"
            />
          </div>

          <div className="studio-field">
            <label>Sub-heading *</label>
            <input
              type="text"
              className="studio-input"
              value={hunt.subHeading}
              onChange={(e) => updateHunt({ subHeading: e.target.value })}
              placeholder="Supporting line under the title"
            />
          </div>
        </div>

        <div className="studio-col-side">
          <div className="studio-side-card">
            <div className="studio-side-eyebrow">Colours</div>
            <div className="studio-pallet-list">
              {COLOR_FIELDS.map(({ key, label }) => (
                <div className="studio-color-row" key={key}>
                  <input
                    type="color"
                    className="studio-color-swatch"
                    value={/^#[0-9a-f]{6}$/i.test(hunt[key]) ? hunt[key] : "#000000"}
                    onChange={(e) => updateHunt({ [key]: e.target.value })}
                  />
                  <div className="studio-color-input-label">{label}</div>
                </div>
              ))}
            </div>
            {buttonContrast != null && (
              <div className={"studio-contrast" + (passesButtonContrast ? " studio-contrast--pass" : " studio-contrast--fail")}>
                <span className="studio-dot" />
                Button {passesButtonContrast ? "passes" : "has low"} text contrast ({buttonContrast.toFixed(1)}:1)
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="studio-field">
        <label>Player instructions *</label>
        <div className="studio-hint" style={{ marginBottom: 10 }}>
          Shown to players before they start and from the Instructions button during the hunt.
        </div>

        <div className="studio-rule-box">
          <div className="studio-rule-list">
            {instructions.map((text, i) => (
              <div key={i} className="studio-rule-row">
                <span className="studio-rule-number">{i + 1}.</span>
                <input
                  type="text"
                  className="studio-rule-input"
                  value={text}
                  maxLength={INSTRUCTIONS_MAX_CHARS}
                  onChange={(e) => setInstruction(i, e.target.value)}
                  placeholder={`Instruction ${i + 1}`}
                />
                <button
                  type="button"
                  className="studio-rule-remove"
                  onClick={() => removeInstruction(i)}
                  disabled={instructions.length <= 1}
                  aria-label="Remove instruction"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="studio-rule-add-btn"
            onClick={addInstruction}
            disabled={instructions.length >= INSTRUCTIONS_MAX_POINTS}
          >
            + Add New Instruction
          </button>

          <div className="studio-rule-notes">
            <div className="studio-rule-note">* Keep {INSTRUCTIONS_MAX_POINTS} instructions at the most.</div>
            <div className="studio-rule-note">* At least 1 instruction is required.</div>
            <div className="studio-rule-note">* Character limit of each instruction is {INSTRUCTIONS_MAX_CHARS}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GameBasics;
